import logger from "@insite/client-framework/Logger";
import { post } from "@insite/client-framework/Services/ApiService";
import { BillToModel, InvoiceModel } from "@insite/client-framework/Types/ApiModels";

// view models
export interface InvoicePaymentModel {
    invoice: InvoiceModel;
    payInFull: boolean;
    paymentAmount: number;
    discountAmount: number;
    enteredPaymentAmount?: string;
    enteredPaymentAmountError?: string;
}

export interface InvoicePaymentCollection {
    invoicePayments: InvoicePaymentModel[];
    achAccountId?: string;
    paymentInformation: any;
    creditCardValues?: InvoiceCreditCard;
    paymentNotes?: string;
    billingAddress?: BillToModel;
    hasSubmitted?: boolean;
    isSubmitting?: boolean;
    overviewErrorMessage?: string;
}

export interface InvoiceCreditCard {
    cardHolderName?: string;
    cardNumber?: string;
    cardType?: string;
    expirationMonth?: number;
    expirationYear?: number;
    securityCode?: string;
    cardHolderNameError?: string;
    cardNumberError?: string;
    cardTypeError?: string;
    securityCodeError?: string;
    expirationError?: string;
    showFormErrors?: boolean;
}

// API Models
export interface InvoicePaymentApiModel {
    SelectedInvoice: SelectedInvoice[];
    AchAccountId?: string;
    CreditCardModel?: CreditCardModel;
    PaymentInformation: any;
    PartialInvoicePaymentCollection: PartialInvoicePaymentCollection[];
}

export interface CreditCardModel {
    CardType: string;
    CardHolderName: string;
    CardNumber: string;
    ExpirationMonth: number;
    ExpirationYear: number;
    SecurityCode: string;
    UseBillingAddress: boolean;
    Address1: string;
    City: string;
    StateAbbreviation: string;
    CountryAbbreviation: string;
    PostalCode: string;
}

export interface SelectedInvoice {
    InvoiceNumber: string;
    PaymentAmount: number;
}

export interface GetInvoicePaymentsParameter {
    invoiceNumbers?: string[];
}

export interface PartialInvoicePaymentCollection {
    InvoiceNumber: string;
    CurrentBalance?: string;
    PayAmount?: string;
    Reason?: string;
}

export interface InvoiceValidationResult {
    isValid: boolean;
    validationMessage: string;
    invoiceProcessingInformation: InvoiceProcessingInformation[];
}

export interface InvoiceProcessingInformation {
    invoiceNumber: string;
    startingBalance: number;
    endingBalance: number;
    requestedPayment: number;
    actualPayment: number;
    termsAmount: number;
    invoiceDate: Date;
    isCredit: boolean;
}

export async function submitInvoicePayment(parameter: InvoicePaymentApiModel) {
    const apiUrl = `${window.origin}/api/auersteel/v1/invoices/pay`;

    const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            SelectedInvoice: [...parameter.SelectedInvoice],
            CreditCardModel: { ...parameter.CreditCardModel },
            AchAccountId: parameter.AchAccountId,
            PaymentInformation: parameter.PaymentInformation,
            PartialInvoicePaymentCollection: parameter.PartialInvoicePaymentCollection
        }),
    };

    // eslint-disable-next-line no-return-await
    return await fetch(apiUrl, requestOptions)
        .then(response => {
                if (response.ok) {
                    return Promise.resolve(true);
                } 
                return Promise.reject(response);                
        })
        .catch(async error => {
            let message: string;
            let errorJson: any;
            if (error?.headers?.get("Content-Type")?.startsWith("application/json")) {
                errorJson = (await error.json()) as { message: string; exceptionMessage?: string };
                message = errorJson.exceptionMessage ?? errorJson.message;
            }
            else {
                message = await error.text();
            }

            logger.error(`Error occurred during fetch: ${message}`);
        });
}

export async function ValidateInvoicePayment(parameter: InvoicePaymentApiModel): Promise<InvoiceValidationResult> {
    const apiUrl = `${window.origin}/api/auersteel/v1/invoices/validate`;

    const result = await post<InvoicePaymentApiModel, InvoiceValidationResult>(apiUrl, parameter);
    return result;
}